import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import OptimizedImage from './OptimizedImage';
import './SradhaBlogs.css';

// Data matching the exact 4 Announcement posts on blogs.sradhatech.com
const FALLBACK_POSTS = [
  {
    id: 'post-1',
    title: 'HireSmart Update Is Here: A Smarter Way to Connect Talent with Opportunity',
    excerpt: 'Making Hiring Simpler for Everyone. At Sradha Technologies, we are constantly improving HireSmart to create a better hiring experience for both employers and job seekers.',
    category: 'Announcement',
    date: 'August 5, 2026',
    slug: 'hiresmart-update-is-here-a-smarter-way-to-connect-talent-with-opportunity',
    url: 'https://blogs.sradhatech.com/announcement/2026/08/05/hiresmart-update-is-here-a-smarter-way-to-connect-talent-with-opportunity/',
    image: 'https://firebasestorage.googleapis.com/v0/b/sradhatech-475479.firebasestorage.app/o/media%2F1785913469230_cover.png?alt=media&token=836e56d6-b841-4c7e-a69f-2d378734cc6b'
  },
  {
    id: 'post-2',
    title: 'Introducing Orchix AI The Data Pipeline Platform That Builds Itself',
    excerpt: 'Sradha Technologies launches Orchix Ai, a zero-code enterprise data platform on Microsoft Fabric, PySpark, and Delta Lake that automates ETL from raw ingestion to analytics-ready output.',
    category: 'Announcement',
    date: 'July 28, 2026',
    slug: 'introducing-orchix-ai-the-data-pipeline-platform-that-builds-itself',
    url: 'https://blogs.sradhatech.com/announcement/2026/07/28/introducing-orchix-ai-the-data-pipeline-platform-that-builds-itself/',
    image: 'https://firebasestorage.googleapis.com/v0/b/sradhatech-475479.firebasestorage.app/o/media%2F1785247346356_orchix_ai_cover_v2.png?alt=media&token=901b3074-66e9-4994-9867-5ffd7aae63ca'
  },
  {
    id: 'post-3',
    title: "Introducing FirmaSearch: Own Your Brand's Presence Inside AI Answers",
    excerpt: 'Sradha Technologies launches FirmaSearch, an AI Answer Engine Optimization (AEO) platform that tracks, analyzes, and improves how brands are cited by ChatGPT, Claude, Gemini, and other AI engines.',
    category: 'Announcement',
    date: 'July 25, 2026',
    slug: 'introducing-firmasearch-own-your-brands-presence-inside-ai-answers',
    url: 'https://blogs.sradhatech.com/announcement/2026/07/25/introducing-firmasearch-own-your-brands-presence-inside-ai-answers/',
    image: 'https://firebasestorage.googleapis.com/v0/b/sradhatech-475479.firebasestorage.app/o/media%2F1785002260140_firmasearch-thumbnail.png?alt=media&token=373d2864-8611-497b-aa59-2b8ae9b877b3'
  },
  {
    id: 'post-4',
    title: 'HireSmart: The Future of Recruitment by Sradha Technologies',
    excerpt: 'Smarter Matches. Faster Results. Better Teams. Transform your hiring workflow with AI-driven candidate matching and automated scheduling.',
    category: 'Announcement',
    date: 'May 2, 2026',
    slug: 'hiresmart-the-future-of-recruitment-by-sradha-technologies',
    url: 'https://blogs.sradhatech.com/announcement/2026/05/02/hiresmart-the-future-of-recruitment-by-sradha-technologies/',
    image: 'https://firebasestorage.googleapis.com/v0/b/sradhatech-475479.firebasestorage.app/o/media%2F1777893900277_Thumbnail.jpg?alt=media&token=b27e70d6-b6e2-4f7c-8c50-2b6e38a6fcf5'
  }
];

const SradhaBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCardsToShow(1);
      } else if (window.innerWidth < 1024) {
        setCardsToShow(2);
      } else if (window.innerWidth < 1400) {
        setCardsToShow(3);
      } else {
        setCardsToShow(4);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Parse direct redirect link for posts
  const getPostUrl = (post) => {
    if (post.url && post.url.startsWith('http')) return post.url;
    if (post.link && post.link.startsWith('http')) return post.link;
    if (post.isRealFirestorePost && post.category && post.date && post.slug) {
      let dateObj;
      if (post.date.seconds) {
        dateObj = new Date(post.date.seconds * 1000);
      } else {
        dateObj = new Date(post.date);
      }

      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const category = post.category.toLowerCase();
        return `https://blogs.sradhatech.com/${category}/${year}/${month}/${day}/${post.slug}`;
      }
    }
    if (post.isRealFirestorePost && post.id && !post.id.startsWith('post-')) {
      return `https://blogs.sradhatech.com/post/${post.id}`;
    }
    return 'https://blogs.sradhatech.com/category/announcement';
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLatestBlogs = async () => {
      try {
        const postsRef = collection(db, 'posts');
        const q = query(postsRef, orderBy('date', 'desc'), limit(50));

        // Timeout race to prevent endless hanging if network or firebase key has latency
        const querySnapshot = await Promise.race([
          getDocs(q),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore fetch timeout')), 2500))
        ]);

        if (isMounted && querySnapshot && !querySnapshot.empty) {
          // Strictly filter for Announcement blogs only
          const announcementDocs = querySnapshot.docs.filter((doc) => {
            const data = doc.data();
            const title = (data.title || '').toString().toLowerCase();
            const cat = (data.category || data.categoryName || data.type || data.tag || '').toString().toLowerCase();
            const catArr = Array.isArray(data.categories)
              ? data.categories.map((c) => String(c).toLowerCase())
              : Array.isArray(data.tags)
              ? data.tags.map((t) => String(t).toLowerCase())
              : [];
            
            return (
              cat.includes('announc') ||
              catArr.some((c) => c.includes('announc')) ||
              title.includes('announc') ||
              title.includes('launch') ||
              title.includes('unveil')
            );
          });

          if (announcementDocs.length > 0) {
            const fetchedBlogs = announcementDocs.map((doc, idx) => {
              const data = doc.data();

              let formattedDate = '';
              if (data.date) {
                const dateObj = data.date.seconds ? new Date(data.date.seconds * 1000) : new Date(data.date);
                if (!isNaN(dateObj.getTime())) {
                  formattedDate = dateObj.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                } else {
                  formattedDate = String(data.date);
                }
              }

              const postImage = data.feature_image || data.featureImage || data.imageUrl || data.coverImage || data.cover_image || data.thumbnail || data.thumbnailUrl || data.image || data.cover || FALLBACK_POSTS[idx % FALLBACK_POSTS.length].image;
              const postSlug = data.slug || doc.id;
              const postUrl = data.url || data.link || (data.slug ? `https://blogs.sradhatech.com/post/${data.slug}` : 'https://blogs.sradhatech.com/category/announcement');

              return {
                id: doc.id,
                title: data.title || '',
                excerpt: data.excerpt || data.summary || data.description || '',
                category: 'Announcement',
                date: formattedDate || 'Recent',
                slug: postSlug,
                url: postUrl,
                isRealFirestorePost: true,
                image: postImage
              };
            });

            // Combine live announcement blogs with fallbacks, deduplicate, and limit to AT MOST 5 recent posts
            const combined = [...fetchedBlogs];
            FALLBACK_POSTS.forEach((fallback) => {
              if (combined.length < 5 && !combined.some((b) => b.id === fallback.id || b.slug === fallback.slug || b.title === fallback.title)) {
                combined.push(fallback);
              }
            });

            const top5AnnouncementBlogs = combined.slice(0, 5);

            if (top5AnnouncementBlogs.length > 0 && isMounted) {
              setBlogs(top5AnnouncementBlogs);
              setLoading(false);
              return;
            }
          }
        }
      } catch (err) {
        console.warn('Firestore fetch failed or timed out, loading static announcement posts:', err);
      }

      if (isMounted) {
        setBlogs(FALLBACK_POSTS.slice(0, 5));
        setLoading(false);
      }
    };

    fetchLatestBlogs();

    return () => {
      isMounted = false;
    };
  }, []);

  const maxIndex = Math.max(0, blogs.length - cardsToShow);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [cardsToShow, blogs.length, maxIndex]);

  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const cardWidth = `calc((100% - ${(cardsToShow - 1) * 2}rem) / ${cardsToShow})`;

  if (loading) {
    const skeletons = Array.from({ length: cardsToShow });
    return (
      <section className="blogs-section loading-skeleton-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Blogs</h2>
            <span className="section-subtitle">Featured Insights</span>
          </div>
        </div>
        <div className="blogs-grid-wrapper">
          <div className="blogs-slider-container">
            <div className="blogs-slider-track">
              <div className="blogs-grid skeleton-grid">
                {skeletons.map((_, idx) => (
                  <div 
                    key={idx} 
                    className="blog-card skeleton-card"
                    style={{ flex: `0 0 ${cardWidth}`, width: cardWidth }}
                  >
                    <div className="blog-card-thumb-wrap skeleton-thumb skeleton-pulse"></div>
                    <div className="blog-card-details">
                      <div className="blog-card-meta">
                        <div className="skeleton-meta-pill skeleton-pulse"></div>
                        <div className="skeleton-meta-date skeleton-pulse"></div>
                      </div>
                      <div className="skeleton-title-line skeleton-pulse"></div>
                      <div className="skeleton-title-line short skeleton-pulse"></div>
                      <div className="skeleton-excerpt-line skeleton-pulse"></div>
                      <div className="skeleton-excerpt-line skeleton-pulse"></div>
                      <div className="skeleton-excerpt-line short skeleton-pulse"></div>
                      <div className="skeleton-btn skeleton-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="blogs-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Our Blogs</h2>
          <span className="section-subtitle">Featured Insights</span>
        </div>
      </div>

      <div className="blogs-grid-wrapper">
        <div className="blogs-slider-container">
          <button 
            className="blogs-carousel-btn prev" 
            onClick={prevSlide} 
            disabled={currentIndex === 0}
            aria-label="Previous blog post"
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>

          <div className="blogs-slider-track">
            <div 
              className="blogs-grid"
              style={{ 
                transform: `translateX(calc(-${currentIndex} * (100% + 2rem) / ${cardsToShow}))`
              }}
            >
              {blogs.map((post) => (
                <div 
                  key={post.id} 
                  className="blog-card"
                  style={{ flex: `0 0 ${cardWidth}`, width: cardWidth }}
                >
                  <a 
                    href={getPostUrl(post)} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="blog-card-thumb-link"
                    aria-label={`Read ${post.title}`}
                  >
                    <div className="blog-card-thumb-wrap">
                      {post.image && post.image.startsWith('/images/') ? (
                        <OptimizedImage
                          src={post.image}
                          alt={post.title}
                          className="blog-card-thumb-img"
                          type="hero"
                        />
                      ) : (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="blog-card-thumb-img"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </div>
                  </a>

                  <div className="blog-card-details">
                    <div className="blog-card-meta">
                      <span className="blog-category-label">{post.category}</span>
                      <span className="blog-pill-tag">{post.category.toLowerCase()}</span>
                      <span className="blog-card-date">{post.date}</span>
                    </div>
                    <a
                      href={getPostUrl(post)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blog-card-title"
                    >
                      {post.title}
                    </a>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <a
                      href={getPostUrl(post)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blog-card-cta-btn"
                    >
                      Read More <i className="fa-solid fa-arrow-right"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button 
            className="blogs-carousel-btn next" 
            onClick={nextSlide} 
            disabled={currentIndex >= maxIndex}
            aria-label="Next blog post"
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        {maxIndex > 0 && (
          <div className="blogs-navigation-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
              <button
                key={idx}
                className={`blogs-nav-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SradhaBlogs;
